/* Branded, contract-grade PDF export (pdfmake, lazy-loaded). Typography echoes the
   HD Studio print language — muted palette, letter-spaced heads, hairline rules,
   a cover page, numbered sections and an execution/acceptance block. */

import { STUDIO, money, defaultEngagement, snapshot } from "./store";
import { totalArea, unitLabel } from "./program";
import { DISCIPLINE_SCOPE, PHASE_OUTLINE, DEFAULT_DISCIPLINES } from "./rates";
import { fmtDate } from "./format";
import { designSteps } from "./nextSteps";
import { auditRows as spaceAuditRows, AUDIT_STATE_LABEL } from "./spaceAudit";
import type { Project, ProjectPackage, Deliverable, DrawingReview, RFI } from "./types";
import type { Estimate, AdditionalService } from "./rates";

/* ---- palette & geometry (borrowed from the contract generator) ---- */
const INK = "#1a1714", BODY = "#2c2825", MUTE = "#6b665e", FAINT = "#9a948a", LINE = "#d8d1c3", CLAY = "#a44a2c";
const A4 = 595.28, MARGIN = 52, CW = A4 - MARGIN * 2;

const rule = (width: number, o: { color?: string; w?: number; top?: number; bottom?: number; x?: number } = {}) =>
  ({ canvas: [{ type: "line", x1: o.x || 0, y1: 0, x2: (o.x || 0) + width, y2: 0, lineWidth: o.w ?? 0.5, lineColor: o.color || LINE }], margin: [0, o.top ?? 0, 0, o.bottom ?? 0] });

const kicker = (t: string, extra = {}) => ({ text: t.toUpperCase(), fontSize: 8, characterSpacing: 2.4, color: MUTE, ...extra });
const para = (t: string) => ({ text: t, alignment: "justify", fontSize: 9.5, color: BODY, lineHeight: 1.5, margin: [0, 0, 0, 8] });
const bullets = (items: string[]): any => items.length
  ? ({ ul: items.map((t) => ({ text: t, fontSize: 9.5, color: BODY, lineHeight: 1.4, margin: [0, 1, 0, 1] })), margin: [4, 2, 0, 8] })
  : para("To be confirmed.");

let secNo = 0;
function resetSecs() { secNo = 0; }
function section(title: string): any[] {
  secNo++;
  return [
    { columns: [{ width: 24, text: `${secNo}.`, fontSize: 12, color: CLAY, bold: true }, { width: "*", text: title, fontSize: 12, bold: true, color: INK }], margin: [0, 18, 0, 5] },
    rule(CW, { color: "#c9c2b4", w: 0.6, bottom: 9 }),
  ];
}

const todayISO = () => new Date().toISOString().slice(0, 10);
function cover(kick: string, title: string, subtitle: string, client: string, dateStr?: string): any[] {
  return [
    { text: "", margin: [0, 70, 0, 0] },
    { text: STUDIO.name, alignment: "center", fontSize: 15, bold: true, color: INK, characterSpacing: 1, margin: [0, 0, 0, 3] },
    { text: "ARCHITECTURE · INTERIORS · VISUALIZATION", alignment: "center", fontSize: 7.5, characterSpacing: 2.4, color: MUTE, margin: [0, 0, 0, 46] },
    kicker(kick, { alignment: "center", margin: [0, 0, 0, 10] }),
    { text: title, alignment: "center", fontSize: 27, color: INK, margin: [0, 0, 0, 10] },
    { ...rule(90, { w: 1.4, color: CLAY, x: (CW - 90) / 2, bottom: 14 }) },
    { text: subtitle, alignment: "center", italics: true, color: MUTE, fontSize: 11.5, margin: [50, 0, 50, 40] },
    { columns: [
      { width: "*", stack: [kicker("Between", { alignment: "center", fontSize: 7 }), { text: STUDIO.name, alignment: "center", fontSize: 11, margin: [0, 4, 0, 0] }] },
      { width: "auto", text: "&", alignment: "center", color: FAINT, fontSize: 12, margin: [10, 6, 10, 0] },
      { width: "*", stack: [kicker("And", { alignment: "center", fontSize: 7 }), { text: client, alignment: "center", fontSize: 11, margin: [0, 4, 0, 0] }] },
    ], margin: [40, 0, 40, 0] },
    { text: fmtDate(dateStr || todayISO()), alignment: "center", color: MUTE, fontSize: 9, margin: [0, 40, 0, 0] },
  ];
}

const styles = {};
function baseDoc(content: any[], title: string, footerLabel: string): any {
  return {
    pageSize: "A4", pageMargins: [MARGIN, 54, MARGIN, 46], info: { title }, styles,
    defaultStyle: { font: "Roboto", fontSize: 9.5, color: BODY, lineHeight: 1.45 },
    header: (cur: number) => cur === 1 ? undefined : { text: footerLabel, alignment: "right", color: FAINT, fontSize: 7, margin: [MARGIN, 28, MARGIN, 0] },
    footer: (cur: number, tot: number) => ({ stack: [{ canvas: [{ type: "line", x1: MARGIN, y1: 0, x2: A4 - MARGIN, y2: 0, lineWidth: 0.5, lineColor: LINE }] }, { columns: [{ text: `${STUDIO.name} · studiogy.design`, fontSize: 7, color: MUTE, margin: [0, 4, 0, 0] }, { text: `${cur} / ${tot}`, alignment: "right", fontSize: 7, color: MUTE, margin: [0, 4, 0, 0] }], margin: [MARGIN, 0, MARGIN, 0] }] }),
    content,
  };
}

function feeTable(rows: [string, string, string][]): any {
  return {
    table: { headerRows: 1, widths: ["*", 60, "auto"], body: [
      [{ text: "STAGE", fontSize: 7, color: MUTE, characterSpacing: 0.8 }, { text: "SHARE", fontSize: 7, color: MUTE, characterSpacing: 0.8, alignment: "center" }, { text: "FEE", fontSize: 7, color: MUTE, characterSpacing: 0.8, alignment: "right" }],
      ...rows.map(([a, b, c]) => [{ text: a, fontSize: 9.5, color: INK }, { text: b, fontSize: 9.5, color: MUTE, alignment: "center" }, { text: c, fontSize: 9.5, alignment: "right" }]),
    ] },
    layout: { hLineWidth: (i: number, n: any) => (i === 0 ? 0 : i === 1 ? 1 : i === n.table.body.length ? 0.9 : 0.5), vLineWidth: () => 0, hLineColor: (i: number, n: any) => (i === 1 || i === n.table.body.length ? "#8a8f98" : LINE), paddingTop: () => 5, paddingBottom: () => 5, paddingLeft: (i: number) => (i === 0 ? 0 : 8) },
    margin: [0, 4, 0, 6],
  };
}

function servicesTable(services: AdditionalService[], ccy: string): any {
  return {
    table: { headerRows: 1, widths: ["*", "auto", "auto"], body: [
      [{ text: "SERVICE", fontSize: 7, color: MUTE, characterSpacing: 0.8 }, { text: "UNIT", fontSize: 7, color: MUTE, characterSpacing: 0.8 }, { text: "RATE", fontSize: 7, color: MUTE, characterSpacing: 0.8, alignment: "right" }],
      ...services.map((s) => [
        { stack: [{ text: s.label, fontSize: 9.5, color: INK }, ...(s.blurb ? [{ text: s.blurb + (s.included ? ` (first ${s.included} included in the fee)` : ""), fontSize: 8, color: MUTE, lineHeight: 1.3, margin: [0, 1, 0, 0] }] : [])] },
        { text: `per ${s.unit}`, fontSize: 9, color: MUTE },
        { text: money(s.rate, ccy), fontSize: 9.5, alignment: "right" },
      ]),
    ] },
    layout: { hLineWidth: (i: number, n: any) => (i === 0 ? 0 : i === 1 ? 1 : i === n.table.body.length ? 0.9 : 0.5), vLineWidth: () => 0, hLineColor: (i: number, n: any) => (i === 1 || i === n.table.body.length ? "#8a8f98" : LINE), paddingTop: () => 6, paddingBottom: () => 6, paddingLeft: (i: number) => (i === 0 ? 0 : 8) },
    margin: [0, 4, 0, 6],
  };
}

function signBlock(clientName: string, signedDate: string | undefined, signatureDataUri: string | undefined, issuedDate: string | undefined): any {
  const gap = 30, lineW = (CW - gap) / 2;
  const col = (role: string, name: string, sig: string | undefined, dateLabel: string, dateVal: string | undefined, italic?: boolean) => ({
    width: "*", stack: [
      { text: `Signed for and on behalf of the ${role}`, bold: true, fontSize: 9, margin: [0, 0, 0, 12] },
      kicker("Name", { fontSize: 7, margin: [0, 4, 0, 1] }), { text: name || " ", fontSize: 10 },
      kicker("Signature", { fontSize: 7, margin: [0, 12, 0, 2] }),
      ...(sig ? [{ image: sig, width: Math.min(170, lineW), margin: [0, 2, 0, 2] }] : italic ? [{ text: STUDIO.principal, italics: true, fontSize: 15, margin: [0, 6, 0, 4] }] : [{ text: "\n", fontSize: 10 }]),
      rule(lineW, { color: INK, w: 0.7, top: 2 }),
      kicker(dateLabel, { fontSize: 7, margin: [0, 12, 0, 2] }), { text: dateVal ? fmtDate(dateVal) : " ", fontSize: 10 },
      rule(lineW, { color: INK, w: 0.7, top: 2 }),
    ],
  });
  return { stack: [kicker("Execution", { fontSize: 7.5, characterSpacing: 1.5, color: MUTE, margin: [0, 6, 0, 12] }),
    { columns: [col("Client", clientName, signatureDataUri, "Date", signedDate), col(STUDIO.name, STUDIO.principal, undefined, "Date", issuedDate, true)], columnGap: gap }], margin: [0, 10, 0, 0] };
}

/* ---- documents ---- */
export function engagementDoc(p: Project, est: Estimate): any {
  resetSecs();
  const eng = p.engagement ?? defaultEngagement();
  const ccy = p.estimate?.currency || p.currency;
  const included = (p.estimate?.disciplines ?? DEFAULT_DISCIPLINES.filter((d) => d.core).map((d) => d.key));
  const content: any[] = [...cover("Letter of Engagement", `Design Services — ${p.name}`, [p.type, p.location, p.area].filter(Boolean).join("  ·  "), `${p.client.name}${p.client.company ? ", " + p.client.company : ""}`, eng?.issuedOn || eng?.signedOn), { text: "", pageBreak: "before" }];

  content.push(...section("The Appointment"));
  content.push(para(`This Letter of Engagement confirms the appointment of ${STUDIO.name} ("the Studio", ${STUDIO.principal}, Principal) by ${p.client.name}${p.client.company ? ` of ${p.client.company}` : ""} ("the Client") for the provision of professional design services in connection with ${p.name} — ${p.type}${p.location ? `, located at ${p.location}` : ""}${p.area ? `, with a gross internal area of approximately ${p.area}` : ""} ("the Project").`));
  content.push(para(`The construction budget on which fees are based is ${money(p.budget, ccy)}. This letter sets out the services, the professional fee, the stages of work, the limitations of the appointment and the terms on which the Studio is engaged.`));

  content.push(...section("Scope of Services"));
  content.push(para("The Studio will provide the following professional services for the Project:"));
  const discMap: Record<string, string> = { architecture: "Architecture", interior: "Interior Design", structural: "Structural Engineering", mep: "Mechanical, Electrical & Plumbing (MEP) Engineering", landscape: "Landscape", consultancy: "Consultancy & Project Management" };
  for (const key of included) {
    if (!DISCIPLINE_SCOPE[key]) continue;
    content.push({ text: discMap[key] || key, fontSize: 10, bold: true, color: INK, margin: [0, 8, 0, 4] });
    content.push(bullets(DISCIPLINE_SCOPE[key]));
  }

  content.push(...section("Professional Fee"));
  content.push(para(`The professional fee for the services above is ${money(est.total, ccy)}, being ${(est.pctOfBudget * 100).toFixed(1)}% of the construction budget. Fees are exclusive of VAT, statutory fees and reimbursable expenses, and are payable stage by stage in accordance with the schedule below.`));
  content.push(feeTable(est.phases.map((ph) => [ph.label, `${ph.pctLabel}%`, money(ph.amount, ccy)])));
  content.push({ columns: [{ text: "Total professional fee", bold: true, fontSize: 10 }, { text: money(est.total, ccy), bold: true, fontSize: 11, alignment: "right", color: INK }], margin: [0, 2, 0, 2] });

  content.push(...section("Stages of Work"));
  content.push(para("The design is developed and the fee earned across the following work stages:"));
  for (const ph of est.phases) {
    content.push({ columns: [{ width: "*", text: ph.label, bold: true, fontSize: 9.5, color: INK }, { width: "auto", text: `${ph.pctLabel}% · ${money(ph.amount, ccy)}`, fontSize: 9, color: MUTE, alignment: "right" }], margin: [0, 8, 0, 3] });
    if (PHASE_OUTLINE[ph.key]) content.push({ text: PHASE_OUTLINE[ph.key], fontSize: 9, color: BODY, lineHeight: 1.45, margin: [0, 0, 0, 2] });
  }

  content.push(...section("Additional Services & Charges"));
  content.push(para("The following services fall outside the agreed scope of this appointment and are charged as and when instructed, at the rates set out below. A written estimate is provided before any additional service is undertaken, and such charges are invoiced in addition to the professional fee."));
  const services = snapshot().rateCard.services ?? [];
  if (services.length) content.push(servicesTable(services, ccy));

  content.push(...section("Limitations of Services"));
  content.push(para("The appointment excludes, and the Studio accepts no responsibility for, the following:"));
  content.push(bullets(eng?.limitations ?? []));

  content.push(...section("Client's Obligations"));
  content.push(bullets(["Provide a clear brief, timely decisions, approvals and information.", "Provide access to the site and any existing surveys or drawings.", "Appoint any other consultants or contractors required for the Project.", "Settle the Studio's invoices in accordance with the payment schedule."]));

  content.push(...section("Intellectual Property"));
  content.push(para("Copyright and all intellectual property in the designs, drawings and documents produced remain vested in the Studio. A licence to use the design for the Project is granted upon settlement of all fees due."));

  content.push(...section("Suspension & Termination"));
  content.push(para("Either party may suspend or terminate this appointment on reasonable written notice. On termination, fees are due for services performed and reasonably committed up to the date of termination, together with any reimbursable expenses incurred."));

  content.push(...section("Terms of Appointment"));
  content.push(para(eng?.terms || ""));

  content.push(...section("Acceptance"));
  content.push(para("By signing below, the Client accepts this appointment, its scope, its stages, the fee and the limitations of services set out above."));
  content.push(signBlock(eng?.signedBy || p.client.name, eng?.signedOn, eng?.signatureDataUri, eng?.issuedOn));

  return baseDoc(content, `Letter of Engagement — ${p.name}`, `Letter of Engagement · ${p.code}`);
}

export function briefDoc(p: Project): any {
  resetSecs();
  const b = p.brief;
  const spaces = b.spaces ?? [];
  const refs = b.references ?? [];
  const content: any[] = [...cover("Design Brief", p.name, [p.type, p.location, p.area].filter(Boolean).join("  ·  "), `${p.client.name}${p.client.company ? ", " + p.client.company : ""}`), { text: "", pageBreak: "before" }];

  content.push(...section("Project Data"));
  content.push({ columns: [
    { width: "*", stack: [kicker("Building type", { fontSize: 7 }), { text: p.type, fontSize: 10, margin: [0, 3, 0, 0] }] },
    { width: "*", stack: [kicker("Floor area", { fontSize: 7 }), { text: p.area || "—", fontSize: 10, margin: [0, 3, 0, 0] }] },
    { width: "*", stack: [kicker("Budget", { fontSize: 7 }), { text: money(p.budget, p.currency), fontSize: 10, margin: [0, 3, 0, 0] }] },
    { width: "*", stack: [kicker("Survey", { fontSize: 7 }), { text: p.surveyPlan === "yes" ? "Available" : p.surveyPlan === "no" ? "Not available" : "To confirm", fontSize: 10, margin: [0, 3, 0, 0] }] },
  ], columnGap: 12, margin: [0, 2, 0, 4] });

  content.push(...section("The Brief"));
  for (const s of b.sections) {
    content.push({ text: s.title, fontSize: 10, bold: true, color: INK, margin: [0, 8, 0, 3] });
    content.push(para(s.body?.trim() ? s.body : "To be developed."));
  }

  if (spaces.length) {
    content.push(...section("Accommodation Schedule"));
    content.push({
      table: { headerRows: 1, widths: ["*", 34, "auto", 46, "auto"], body: [
        [{ text: "SPACE", fontSize: 7, color: MUTE }, { text: "QTY", fontSize: 7, color: MUTE, alignment: "center" }, { text: "ZONE", fontSize: 7, color: MUTE }, { text: "AREA", fontSize: 7, color: MUTE, alignment: "right" }, { text: "SUBTOTAL", fontSize: 7, color: MUTE, alignment: "right" }],
        ...spaces.map((s) => [{ text: s.name, fontSize: 9, color: INK }, { text: String(s.qty), fontSize: 9, alignment: "center" }, { text: s.zone ?? "—", fontSize: 8, color: MUTE }, { text: String(s.area), fontSize: 9, alignment: "right" }, { text: (s.qty * s.area).toLocaleString(), fontSize: 9, alignment: "right" }]),
        [{ text: "Gross", colSpan: 4, bold: true, fontSize: 9.5 }, {}, {}, {}, { text: `${totalArea(spaces).toLocaleString()} ${unitLabel(b.areaUnit)}`, bold: true, fontSize: 9.5, alignment: "right" }],
      ] },
      layout: { hLineWidth: (i: number, n: any) => (i === 0 ? 0 : i === 1 ? 1 : i === n.table.body.length ? 0.9 : 0.5), vLineWidth: () => 0, hLineColor: (i: number, n: any) => (i === 1 || i === n.table.body.length ? "#8a8f98" : LINE), paddingTop: () => 4.5, paddingBottom: () => 4.5, paddingLeft: (i: number) => (i === 0 ? 0 : 6) },
      margin: [0, 4, 0, 6],
    });
  }
  if (refs.length) {
    content.push(...section("Design References"));
    content.push(bullets(refs.map((r) => `${r.title}${r.note ? " — " + r.note : ""}`)));
  }
  return baseDoc(content, `Design Brief — ${p.name}`, `Design Brief · ${p.code}`);
}

/* ---------------------------------------------------------- designer pack --
   Everything a designer needs to take the concept on: the project, the brief,
   the accommodation, the direction (references, mood, palette), the scope in
   fee terms, what to produce, and what's still unresolved. */

/** Mood images live either as data URIs or as same-origin /api/img URLs.
 *  pdfmake needs data, so pull anything remote down first. */
async function asDataUri(src: string): Promise<string | null> {
  if (src.startsWith("data:")) return src;
  try {
    const r = await fetch(src, { credentials: "same-origin" });
    if (!r.ok) return null;
    const blob = await r.blob();
    return await new Promise<string | null>((res) => {
      const fr = new FileReader();
      fr.onload = () => res(typeof fr.result === "string" ? fr.result : null);
      fr.onerror = () => res(null);
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

/** The pack strips every figure we generate, but the brief is the client's own
 *  prose — we won't rewrite it. Flag the sections that talk money so the studio
 *  can edit them before the pack goes out. */
const MONEY_PROSE = /\bbudget|\bcost|\bprice|\bfees?\b|\bspend\b|\$|\bUSD\b|\bGYD\b|\bmillion\b/i;
export function briefMoneyMentions(p: Project): string[] {
  return (p.brief?.sections ?? []).filter((s) => MONEY_PROSE.test(s.body || "")).map((s) => s.title);
}

export async function designerPackDoc(p: Project): Promise<any> {
  resetSecs();
  const b = p.brief;
  const spaces = b.spaces ?? [];
  const refs = b.references ?? [];
  const mood = p.moodboard?.images ?? [];
  const swatches = p.moodboard?.swatches ?? [];
  const included = p.estimate?.disciplines ?? DEFAULT_DISCIPLINES.filter((d) => d.core).map((d) => d.key);
  const conceptSheets = (p.deliverables ?? []).filter((d) => d.phase === "Conceptual");
  // No fee, no budget, no invoices — a designer is briefed on the design, not
  // on what the client is paying.
  const steps = designSteps(p);

  const content: any[] = [
    ...cover("Concept Design Pack", p.name, [p.type, p.location, p.area].filter(Boolean).join("  ·  "), `${p.client.name}${p.client.company ? ", " + p.client.company : ""}`),
    { text: "", pageBreak: "before" },
  ];

  content.push(...section("Project Data"));
  const facts: [string, string][] = [
    ["Building type", (p.types?.length ? p.types.join(" · ") : p.type) || "—"],
    ["Location", p.location || "—"],
    ["Floor area", p.area || (p.gia ? `${p.gia.toLocaleString()} ${unitLabel(p.giaUnit)}` : "—")],
    ["Survey plan", p.surveyPlan === "yes" ? "Available" : p.surveyPlan === "no" ? "Not available" : "To confirm"],
    ["Site coordinates", p.geo ? `${p.geo.lat}, ${p.geo.lng}` : "—"],
    ["Client", `${p.client.name}${p.client.company ? ` · ${p.client.company}` : ""}`],
    ["Appointed", fmtDate(p.createdOn)],
  ];
  content.push({
    table: { widths: ["auto", "*"], body: facts.map(([k, v]) => [
      { text: k.toUpperCase(), fontSize: 7, characterSpacing: 1.2, color: MUTE, margin: [0, 2, 0, 0] },
      { text: v, fontSize: 10, color: INK },
    ]) },
    layout: { hLineWidth: (i: number, n: any) => (i === 0 || i === n.table.body.length ? 0 : 0.5), vLineWidth: () => 0, hLineColor: () => LINE, paddingTop: () => 5, paddingBottom: () => 5, paddingLeft: (i: number) => (i === 0 ? 0 : 12) },
    margin: [0, 2, 0, 4],
  });

  content.push(...section("The Brief"));
  for (const s of b.sections) {
    content.push({ text: s.title, fontSize: 10, bold: true, color: INK, margin: [0, 8, 0, 3] });
    content.push(para(s.body?.trim() ? s.body : "Not yet answered — worth chasing before the concept firms up."));
  }

  if (spaces.length) {
    content.push(...section("Accommodation to Design"));
    content.push(para(`${spaces.length} spaces · ${totalArea(spaces).toLocaleString()} ${unitLabel(b.areaUnit)} total. This is the target the concept has to hit.`));
    content.push({
      table: { headerRows: 1, widths: ["*", 30, "auto", 42, 44], body: [
        [{ text: "SPACE", fontSize: 7, color: MUTE }, { text: "QTY", fontSize: 7, color: MUTE, alignment: "center" }, { text: "ZONE", fontSize: 7, color: MUTE }, { text: "EACH", fontSize: 7, color: MUTE, alignment: "right" }, { text: "TOTAL", fontSize: 7, color: MUTE, alignment: "right" }],
        ...spaces.map((s) => [
          { stack: [{ text: s.name, fontSize: 9, color: INK }, ...(s.note ? [{ text: s.note, fontSize: 7.5, color: MUTE, margin: [0, 1, 0, 0] }] : [])] },
          { text: String(s.qty), fontSize: 9, alignment: "center" },
          { text: s.zone ?? "—", fontSize: 8, color: MUTE },
          { text: String(s.area), fontSize: 9, alignment: "right" },
          { text: (s.qty * s.area).toLocaleString(), fontSize: 9, alignment: "right" },
        ]),
        [{ text: "Gross", colSpan: 4, bold: true, fontSize: 9.5 }, {}, {}, {}, { text: `${totalArea(spaces).toLocaleString()}`, bold: true, fontSize: 9.5, alignment: "right" }],
      ] },
      layout: { hLineWidth: (i: number, n: any) => (i === 0 ? 0 : i === 1 ? 1 : i === n.table.body.length ? 0.9 : 0.5), vLineWidth: () => 0, hLineColor: (i: number, n: any) => (i === 1 || i === n.table.body.length ? "#8a8f98" : LINE), paddingTop: () => 4.5, paddingBottom: () => 4.5, paddingLeft: (i: number) => (i === 0 ? 0 : 6) },
      margin: [0, 4, 0, 6],
    });
  }

  /* ---- direction: the mood board and references are the brief for the eye ---- */
  content.push(...section("Mood & Direction"));
  if (!refs.length && !swatches.length && !mood.length) {
    content.push(para("Nothing gathered yet — worth collecting references with the client before the concept starts, or the direction is a guess."));
  }

  if (mood.length) {
    const picked = mood.slice(0, 12);
    const uris = await Promise.all(picked.map((m) => asDataUri(m.src)));
    const shown = picked.map((m, i) => ({ m, src: uris[i] })).filter((x) => x.src) as { m: typeof mood[number]; src: string }[];
    if (shown.length) {
      content.push(para(`${mood.length} image${mood.length > 1 ? "s" : ""} the client is drawn to${mood.length > shown.length ? ` (${shown.length} shown)` : ""} — this is the feeling to design toward.`));
      const W = (CW - 24) / 3;
      for (let i = 0; i < shown.length; i += 3) {
        content.push({
          columns: shown.slice(i, i + 3).map((x) => ({
            width: W,
            stack: [
              { image: x.src, fit: [W, 108] },
              ...(x.m.caption ? [{ text: x.m.caption, fontSize: 7.5, color: MUTE, margin: [0, 3, 0, 0] }] : []),
            ],
          })),
          columnGap: 12, margin: [0, 0, 0, 12],
        });
      }
    } else {
      content.push(para(`${mood.length} mood image${mood.length > 1 ? "s" : ""} are on the board but couldn't be embedded here — open the project's mood board to see them.`));
    }
  }

  if (swatches.length) {
    content.push({ text: "Feature palette", fontSize: 10, bold: true, color: INK, margin: [0, 6, 0, 6] });
    content.push({
      columns: swatches.slice(0, 6).map((s) => ({
        width: "auto",
        stack: [
          { canvas: [{ type: "rect", x: 0, y: 0, w: 54, h: 30, r: 3, color: s.hex, lineWidth: 0.5, lineColor: LINE }] },
          { text: s.name, fontSize: 8, color: INK, margin: [0, 4, 0, 0] },
          { text: [s.material, s.hex].filter(Boolean).join(" · "), fontSize: 7, color: MUTE },
        ],
        margin: [0, 0, 10, 0],
      })),
      margin: [0, 0, 0, 10],
    });
  }

  if (refs.length) {
    content.push({ text: "References the client gave us", fontSize: 10, bold: true, color: INK, margin: [0, 6, 0, 4] });
    content.push(bullets(refs.map((r) => `${r.title}${r.note ? " — " + r.note : ""}${r.url ? ` (${r.url})` : ""}`)));
  }

  content.push(...section("Scope of Design"));
  content.push(para("What the studio is appointed to deliver — design to what's listed here and no further."));
  const discMap: Record<string, string> = { architecture: "Architecture", interior: "Interior Design", structural: "Structural Engineering", mep: "MEP Engineering", landscape: "Landscape", consultancy: "Consultancy & PM" };
  for (const key of included) {
    if (!DISCIPLINE_SCOPE[key]) continue;
    content.push({ text: discMap[key] || key, fontSize: 10, bold: true, color: INK, margin: [0, 8, 0, 4] });
    content.push(bullets(DISCIPLINE_SCOPE[key]));
  }

  if (conceptSheets.length) {
    content.push(...section("Produce at Concept"));
    content.push(para(`${conceptSheets.length} sheets are scheduled for this stage.`));
    content.push(servicesLikeTable(conceptSheets.map((d) => [d.code, d.title, [d.scale, d.sheet].filter(Boolean).join(" · ") || "—"])));
  }

  /* The engagement's limitations are written for the client and are mostly
     commercial ("charged per visit", "fees exclude VAT"). Keep only the ones
     that tell a designer what not to draw. */
  const COMMERCIAL = /\bfees?\b|\bcharg|\bcost|\bprice|\brate|\binvoic|\bpaid\b|\bpayment|\bVAT\b|\bpremium|\bbilled\b|\bper (image|visit|sheet|change)\b/i;
  const limits = ((p.engagement ?? defaultEngagement()).limitations ?? []).filter((l) => !COMMERCIAL.test(l));
  if (limits.length) {
    content.push(...section("Out of Scope"));
    content.push(para("Don't design these in — they're outside the appointment:"));
    content.push(bullets(limits));
  }

  if (steps.length) {
    content.push(...section("Still Unresolved"));
    content.push(para("Open items that could move the target. Worth resolving before the concept is committed."));
    content.push(bullets(steps.map((s) => `${s.label} — ${s.who === "client" ? "with the client" : "with the studio"}${s.detail ? `. ${s.detail}` : ""}`)));
  }

  return baseDoc(content, `Concept Design Pack — ${p.name}`, `Concept Design Pack · ${p.code}`);
}

/** A light 3-column table (code / title / meta) for sheet lists. */
function servicesLikeTable(rows: [string, string, string][]): any {
  return {
    table: { headerRows: 1, widths: ["auto", "*", "auto"], body: [
      [{ text: "SHEET", fontSize: 7, color: MUTE }, { text: "TITLE", fontSize: 7, color: MUTE }, { text: "SCALE", fontSize: 7, color: MUTE, alignment: "right" }],
      ...rows.map(([a, bb, c]) => [{ text: a, fontSize: 9, color: INK }, { text: bb, fontSize: 9 }, { text: c, fontSize: 8, color: MUTE, alignment: "right" }]),
    ] },
    layout: { hLineWidth: (i: number, n: any) => (i === 0 ? 0 : i === 1 ? 1 : i === n.table.body.length ? 0.9 : 0.5), vLineWidth: () => 0, hLineColor: (i: number, n: any) => (i === 1 || i === n.table.body.length ? "#8a8f98" : LINE), paddingTop: () => 5, paddingBottom: () => 5, paddingLeft: (i: number) => (i === 0 ? 0 : 8) },
    margin: [0, 4, 0, 6],
  };
}

/* ---- render (lazy pdfmake) ---- */
let vfsReady = false;
async function pm(): Promise<any> {
  const pmMod: any = await import("pdfmake/build/pdfmake");
  const pdfMake = pmMod.default ?? pmMod;
  if (!vfsReady) { const vfsMod: any = await import("pdfmake/build/vfs_fonts"); pdfMake.addVirtualFileSystem(vfsMod.default ?? vfsMod); vfsReady = true; }
  return pdfMake;
}
export async function pdfBlobUrl(docDef: any): Promise<string> {
  const pdfMake = await pm();
  const blob: Blob = await pdfMake.createPdf(docDef).getBlob();
  return URL.createObjectURL(blob);
}
async function download(docDef: any, filename: string) {
  const url = await pdfBlobUrl(docDef);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
export const downloadBriefPdf = (p: Project) => download(briefDoc(p), `${p.name.replace(/[^\w]+/g, "_")}_brief.pdf`);
export const downloadEngagementPdf = (p: Project, est: Estimate) => download(engagementDoc(p, est), `${p.name.replace(/[^\w]+/g, "_")}_engagement.pdf`);

/* ---- invoice — a properly formatted, printable tax invoice ---- */
export function invoiceDoc(p: Project, inv: import("./types").Invoice): any {
  const ccy = p.currency;
  const received = (inv.payments ?? []).filter((x) => (x.status ?? "confirmed") === "confirmed").reduce((n, x) => n + x.amount, 0);
  const balance = Math.max(0, inv.amount - received);
  const hasTax = typeof inv.net === "number" && typeof inv.taxRate === "number" && inv.taxRate > 0;
  const moneyRow = (label: string, value: string, o: { bold?: boolean; color?: string; big?: boolean } = {}) => ({
    columns: [
      { width: "*", text: label, fontSize: o.big ? 10.5 : 9.5, bold: o.bold, color: o.color ?? BODY },
      { width: "auto", text: value, fontSize: o.big ? 12 : 9.5, bold: o.bold, alignment: "right", color: o.color ?? INK },
    ], margin: [0, 3, 0, 3],
  });
  return baseDoc([
    /* letterhead */
    { columns: [
      { width: "*", stack: [
        { text: STUDIO.name, fontSize: 15, bold: true, color: INK, characterSpacing: 0.8 },
        { text: "ARCHITECTURE · INTERIORS · VISUALIZATION", fontSize: 6.8, characterSpacing: 2.2, color: MUTE, margin: [0, 3, 0, 0] },
        { text: `studiogy.design · ${STUDIO.email}`, fontSize: 8, color: MUTE, margin: [0, 6, 0, 0] },
      ] },
      { width: "auto", stack: [
        kicker(inv.status === "paid" ? "Receipt · Tax invoice" : "Tax invoice", { alignment: "right" }),
        { text: inv.ref, fontSize: 19, bold: true, color: CLAY, alignment: "right", margin: [0, 4, 0, 0] },
      ] },
    ], margin: [0, 0, 0, 14] },
    rule(CW, { w: 0.9, color: "#c9c2b4", bottom: 16 }),
    /* parties + dates */
    { columns: [
      { width: "*", stack: [
        kicker("Billed to", { margin: [0, 0, 0, 4] }),
        { text: p.client.company || p.client.name, fontSize: 11, bold: true, color: INK },
        ...(p.client.company && p.client.name !== p.client.company ? [{ text: `Attn: ${p.client.name}`, fontSize: 9, color: BODY, margin: [0, 2, 0, 0] }] : []),
        { text: p.client.email, fontSize: 8.5, color: MUTE, margin: [0, 2, 0, 0] },
      ] },
      { width: "*", stack: [
        kicker("Project", { margin: [0, 0, 0, 4] }),
        { text: p.name, fontSize: 10.5, bold: true, color: INK },
        { text: `${p.code} · ${p.location}`, fontSize: 8.5, color: MUTE, margin: [0, 2, 0, 0] },
      ] },
      { width: "auto", stack: [
        kicker("Issued", { margin: [0, 0, 0, 3] }), { text: fmtDate(inv.issuedOn), fontSize: 9.5 },
        kicker("Due", { margin: [0, 8, 0, 3] }), { text: fmtDate(inv.dueOn), fontSize: 9.5, bold: inv.status !== "paid" },
      ] },
    ], columnGap: 24, margin: [0, 0, 0, 20] },
    /* the lines — one for a fee stage, one per receipt for reimbursables */
    { table: { headerRows: 1, widths: ["*", "auto"], body: [
      [{ text: "DESCRIPTION", fontSize: 7, color: MUTE, characterSpacing: 0.8 }, { text: "AMOUNT", fontSize: 7, color: MUTE, characterSpacing: 0.8, alignment: "right" }],
      ...(inv.items?.length
        ? inv.items.map((it) => [
            { stack: [{ text: it.label, fontSize: 9.5, color: INK }, ...(it.date || it.note ? [{ text: [it.date ? fmtDate(it.date) : "", it.note ?? ""].filter(Boolean).join(" · "), fontSize: 7.5, color: MUTE, margin: [0, 1, 0, 0] }] : [])], margin: [0, 2, 0, 2] },
            { text: money(it.amount, ccy), fontSize: 9.5, alignment: "right", margin: [0, 2, 0, 2] },
          ])
        : [[{ text: inv.title, fontSize: 10, color: INK, margin: [0, 2, 0, 2] }, { text: money(hasTax ? inv.net! : inv.amount, ccy), fontSize: 10, alignment: "right", margin: [0, 2, 0, 2] }]]),
    ] },
      layout: { hLineWidth: (i: number, n: any) => (i === 0 ? 0 : i === 1 ? 1 : 0.5), vLineWidth: () => 0, hLineColor: () => LINE, paddingTop: () => 6, paddingBottom: () => 6, paddingLeft: (i: number) => (i === 0 ? 0 : 8) },
      margin: [0, 0, 0, 8] },
    /* totals */
    { columns: [{ width: "*", text: "" }, { width: 250, stack: [
      ...(hasTax ? [
        moneyRow("Subtotal", money(inv.net!, ccy)),
        moneyRow(`${inv.taxLabel || "Tax"} @ ${Math.round(inv.taxRate! * 100)}%`, money(inv.amount - inv.net!, ccy)),
        rule(250, { top: 3, bottom: 3 }),
      ] : []),
      moneyRow("Invoice total", money(inv.amount, ccy), { bold: true }),
      ...(received > 0 ? [
        moneyRow("Received to date", `− ${money(received, ccy)}`, { color: MUTE }),
        rule(250, { top: 3, bottom: 3 }),
        moneyRow(inv.status === "paid" || balance === 0 ? "Balance — settled" : "Balance due", money(balance, ccy), { bold: true, big: true, color: balance === 0 ? "#5c7a3f" : CLAY }),
      ] : [
        rule(250, { top: 3, bottom: 3 }),
        moneyRow(inv.status === "paid" ? "Settled — thank you" : "Amount due", money(balance, ccy), { bold: true, big: true, color: inv.status === "paid" ? "#5c7a3f" : CLAY }),
      ]),
    ] }] },
    /* payments received */
    ...((inv.payments ?? []).length ? [
      kicker("Payments received", { margin: [0, 22, 0, 6] }),
      { table: { widths: ["auto", "*", "auto"], body: (inv.payments ?? []).map((x) => ([
        { text: fmtDate(x.on), fontSize: 8.5, color: MUTE },
        { text: [x.method, x.reference, (x.status ?? "confirmed") === "claimed" ? "reported by client — awaiting confirmation" : ""].filter(Boolean).join(" · ") || "Payment", fontSize: 8.5, color: BODY },
        { text: money(x.amount, ccy), fontSize: 8.5, alignment: "right" },
      ])) }, layout: { hLineWidth: () => 0.4, vLineWidth: () => 0, hLineColor: () => LINE, paddingTop: () => 4, paddingBottom: () => 4 } },
    ] : []),
    { text: inv.payUrl ? `Pay online: ${inv.payUrl}` : `Please quote ${inv.ref} with your payment.`, fontSize: 8.5, color: MUTE, margin: [0, 24, 0, 0] },
    { text: inv.kind === "expenses" ? `Costs incurred on the client's behalf for ${p.name}, billed at cost${inv.items?.some((it) => /handling/.test(it.note ?? "")) ? " plus the agreed handling" : ""}; receipts are on the project record. Questions: ${STUDIO.email}.` : `Raised against the agreed fee for ${p.name}. Questions about this invoice: ${STUDIO.email}.`, fontSize: 8, color: FAINT, margin: [0, 4, 0, 0] },
  ], `${inv.ref} — ${p.name}`, `${inv.ref} · ${p.name}`);
}
export const downloadInvoicePdf = (p: Project, inv: import("./types").Invoice) =>
  download(invoiceDoc(p, inv), `${inv.ref.replace(/[^\w]+/g, "_")}_${p.name.replace(/[^\w]+/g, "_")}.pdf`);

/* ---- the space report — target vs designed, printable ---- */
export function spaceReportDoc(p: Project): any {
  resetSecs();
  const audit = spaceAuditRows(p);
  const unit = unitLabel(p.brief.areaUnit ?? "sqft");
  const totalDelta = audit.measured > 0 && audit.measuredTargetTotal > 0
    ? ((audit.designedTotal - audit.measuredTargetTotal) / audit.measuredTargetTotal) * 100 : null;
  const stateColor = (s: string) => (s === "ok" ? "#5c7a3f" : s === "near" ? "#a07a1e" : s === "off" ? CLAY : FAINT);
  return baseDoc([
    ...cover("Design stage report", "Target vs Designed Space", `How the designed scheme answers the approved brief, space by space — ${p.name}.`, p.client.company || p.client.name),
    { text: "", pageBreak: "after" },
    ...section("The comparison"),
    para(`Each space carries the brief's target (quantity × net area, in ${unit}) beside what the current design provides. A line within ±5% reads as on target; within ±15% as close; beyond that as off target and worth a conversation. Spaces not yet measured are simply not yet drawn to a measurable stage.`),
    { table: { headerRows: 1, widths: ["*", "auto", "auto", "auto", "auto"], body: [
      ["SPACE", `TARGET (${unit.toUpperCase()})`, `DESIGNED (${unit.toUpperCase()})`, "Δ", "READING"].map((t) => ({ text: t, fontSize: 7, color: MUTE, characterSpacing: 0.8 })),
      ...audit.rows.map((r) => ([
        { text: `${r.name}${r.targetQty > 1 ? `  ×${r.targetQty}` : ""}`, fontSize: 9.5, color: INK },
        { text: `${r.targetTotal.toLocaleString()}  (${r.targetQty} × ${r.targetArea})`, fontSize: 9, alignment: "right" },
        { text: r.designedTotal != null ? `${r.designedTotal.toLocaleString()}  (${r.designedQty} × ${r.designedArea})` : "—", fontSize: 9, alignment: "right" },
        { text: r.deltaPct != null ? `${r.deltaPct > 0 ? "+" : ""}${r.deltaPct.toFixed(1)}%` : "—", fontSize: 9, alignment: "right", color: stateColor(r.state) },
        { text: AUDIT_STATE_LABEL[r.state], fontSize: 8.5, color: stateColor(r.state) },
      ])),
      [
        { text: "Total", fontSize: 9.5, bold: true },
        { text: audit.targetTotal.toLocaleString(), fontSize: 9.5, bold: true, alignment: "right" },
        { text: audit.measured ? audit.designedTotal.toLocaleString() : "—", fontSize: 9.5, bold: true, alignment: "right" },
        { text: totalDelta != null ? `${totalDelta > 0 ? "+" : ""}${totalDelta.toFixed(1)}%` : "—", fontSize: 9.5, bold: true, alignment: "right" },
        { text: `${audit.measured}/${audit.rows.length} measured`, fontSize: 8, color: MUTE },
      ],
    ] },
      layout: { hLineWidth: (i: number, n: any) => (i === 0 ? 0 : i === 1 ? 1 : i === n.table.body.length - 1 ? 0.9 : 0.4), vLineWidth: () => 0, hLineColor: () => LINE, paddingTop: () => 5, paddingBottom: () => 5, paddingLeft: (i: number) => (i === 0 ? 0 : 8) },
      margin: [0, 6, 0, 10] },
    ...(p.briefChecklist ? [
      ...section("The brief, answered"),
      para(`${p.briefChecklist.items.filter((i) => i.done).length} of ${p.briefChecklist.items.length} brief lines are checked off as answered by the design.`),
      bullets(p.briefChecklist.items.filter((i) => i.kind === "requirement").map((i) => `${i.done ? "✓" : "—"}  ${i.label}${i.note ? ` — ${i.note}` : ""}`)),
    ] : []),
  ], `Space report — ${p.name}`, `Space report · ${p.name}`);
}
export const downloadSpaceReportPdf = (p: Project) =>
  download(spaceReportDoc(p), `${p.name.replace(/[^\w]+/g, "_")}_space_report.pdf`);

/* ---- drawing transmittal (a formal record of a shared package) ---- */
const PKG_AUDIENCE_LABEL: Record<ProjectPackage["audience"], string> = {
  client: "Client",
  consultant: "Consultant",
  contractor: "Contractor",
};

export function transmittalDoc(p: Project, pkg: ProjectPackage): any {
  resetSecs();
  const all = p.deliverables || [];
  const byId = new Map(all.map((d) => [d.id, d]));
  const sheets = pkg.deliverableIds.map((id) => byId.get(id)).filter(Boolean) as Deliverable[];
  const disciplines = Array.from(new Set(sheets.map((d) => d.discipline).filter(Boolean)));
  const recipient = pkg.recipientName || pkg.recipientEmail || PKG_AUDIENCE_LABEL[pkg.audience];

  const metaRow = (label: string, value: string) => ({
    columns: [
      { width: 96, ...kicker(label, { fontSize: 7, margin: [0, 1, 0, 0] }) },
      { width: "*", text: value || "—", fontSize: 9.5, color: INK },
    ],
    margin: [0, 0, 0, 5],
  });

  const sheetTable = {
    table: {
      headerRows: 1, widths: [64, "*", 82, 40],
      body: [
        [
          { text: "NUMBER", fontSize: 7, color: MUTE, characterSpacing: 0.8 },
          { text: "TITLE", fontSize: 7, color: MUTE, characterSpacing: 0.8 },
          { text: "DISCIPLINE", fontSize: 7, color: MUTE, characterSpacing: 0.8 },
          { text: "REV", fontSize: 7, color: MUTE, characterSpacing: 0.8, alignment: "center" },
        ],
        ...sheets.map((d) => [
          { text: d.code || "—", fontSize: 9, color: INK, bold: true },
          { stack: [
            { text: d.title || "Untitled sheet", fontSize: 9, color: INK },
            ...(d.scale || d.sheet ? [{ text: [d.sheet, d.scale].filter(Boolean).join(" · "), fontSize: 7.5, color: MUTE, margin: [0, 1, 0, 0] }] : []),
          ] },
          { text: d.discipline || "—", fontSize: 9, color: BODY },
          { text: d.revision || "—", fontSize: 9, color: BODY, alignment: "center" },
        ]),
      ],
    },
    layout: { hLineWidth: (i: number, n: any) => (i === 0 ? 0 : i === 1 ? 1 : i === n.table.body.length ? 0.9 : 0.5), vLineWidth: () => 0, hLineColor: (i: number, n: any) => (i === 1 || i === n.table.body.length ? "#8a8f98" : LINE), paddingTop: () => 5, paddingBottom: () => 5, paddingLeft: (i: number) => (i === 0 ? 0 : 8) },
    margin: [0, 4, 0, 6],
  };

  const content: any[] = [
    ...cover("Drawing Transmittal", pkg.title || "Drawing Package", `${PKG_AUDIENCE_LABEL[pkg.audience]} issue · ${pkg.ref}`, recipient, pkg.sharedAt),
    { text: "", pageBreak: "before" },
    ...section("Transmittal record"),
    metaRow("Project", `${p.name}${p.code ? ` · ${p.code}` : ""}`),
    metaRow("Package", `${pkg.ref} — ${pkg.title || "Drawing package"}`),
    metaRow("Issued to", recipient),
    metaRow("For", PKG_AUDIENCE_LABEL[pkg.audience]),
    metaRow("Issued on", fmtDate(pkg.sharedAt)),
    metaRow("Issued by", pkg.sharedBy || STUDIO.name),
    ...(disciplines.length ? [metaRow("Disciplines", disciplines.join(", "))] : []),
    ...(pkg.note ? [{ text: pkg.note, fontSize: 9.5, color: BODY, italics: true, lineHeight: 1.5, margin: [0, 6, 0, 0] }] : []),
    ...section(`Sheets transmitted — ${sheets.length}`),
    sheets.length ? sheetTable : para("No sheets recorded on this package."),
    { text: "This transmittal is a record of the drawings issued in the package above. The recipient should confirm receipt and verify the sheet count and revisions against this schedule.", fontSize: 8, color: MUTE, italics: true, lineHeight: 1.4, margin: [0, 4, 0, 0] },
    ...(pkg.receipt ? [{ text: `Receipt confirmed by ${pkg.receipt.by} on ${fmtDate(pkg.receipt.at)}.`, fontSize: 9, color: CLAY, margin: [0, 8, 0, 0] }] : []),
    signBlock(pkg.receipt?.by || recipient, pkg.receipt?.at, undefined, pkg.sharedAt),
  ];

  return baseDoc(content, `${p.code || p.name} — Transmittal ${pkg.ref}`, `${p.name} · Transmittal ${pkg.ref}`);
}

export const downloadTransmittalPdf = (p: Project, pkg: ProjectPackage) =>
  download(transmittalDoc(p, pkg), `${(p.code || p.name).replace(/[^\w]+/g, "_")}_${pkg.ref}_transmittal.pdf`);

/* A drawing-review / comment-response schedule in construction-drawing format —
   every change the client raised, the studio's implemented response, the revision
   it lands on, and whether the client has approved it. */
export function reviewScheduleDoc(p: Project, review: DrawingReview): any {
  resetSecs();
  const rows: { n: number; sheet: string; title: string; by: string; on: string; onRev: string; req: string; resp?: string; respRev?: string; respOn?: string; approved: boolean; approvedBy?: string }[] = [];
  let n = 0;
  for (const s of review.sheets) for (const m of s.remarks) {
    if (m.author !== "client") continue;
    n++;
    rows.push({ n, sheet: s.code, title: s.title, by: m.authorName, on: m.createdOn, onRev: m.onRevision, req: m.body,
      resp: m.response?.body, respRev: m.response?.revision, respOn: m.response?.respondedOn,
      approved: !!m.response?.accepted, approvedBy: m.response?.accepted?.by });
  }
  const responded = rows.filter((r) => r.resp).length;
  const approved = rows.filter((r) => r.approved).length;

  const metaRow = (label: string, value: string) => ({
    columns: [{ width: 96, ...kicker(label, { fontSize: 7, margin: [0, 1, 0, 0] }) }, { width: "*", text: value || "—", fontSize: 9.5, color: INK }],
    margin: [0, 0, 0, 5],
  });
  const th = (t: string, alignment?: string) => ({ text: t, fontSize: 7, color: MUTE, characterSpacing: 0.8, alignment });
  const table = {
    table: {
      headerRows: 1, widths: [16, 54, "*", "*", 30, 52],
      body: [
        [th("#"), th("SHEET"), th("CHANGE REQUESTED"), th("RESPONSE — CHANGE MADE"), th("REV", "center"), th("STATUS", "center")],
        ...rows.map((r) => [
          { text: String(r.n), fontSize: 8.5, color: MUTE },
          { stack: [{ text: r.sheet || "—", fontSize: 8.5, color: INK, bold: true }, { text: r.title, fontSize: 7, color: MUTE, margin: [0, 1, 0, 0] }] },
          { stack: [{ text: r.req, fontSize: 8.5, color: BODY, lineHeight: 1.3 }, { text: `${r.by} · ${fmtDate(r.on)}${r.onRev ? ` · rev ${r.onRev}` : ""}`, fontSize: 6.8, color: FAINT, margin: [0, 1, 0, 0] }] },
          { stack: [{ text: r.resp || "Not yet responded", fontSize: 8.5, color: r.resp ? BODY : FAINT, italics: !r.resp, lineHeight: 1.3 }, ...(r.respOn ? [{ text: fmtDate(r.respOn), fontSize: 6.8, color: FAINT, margin: [0, 1, 0, 0] }] : [])] },
          { text: r.respRev || "—", fontSize: 8.5, color: BODY, alignment: "center" },
          { text: r.approved ? "APPROVED" : r.resp ? "Addressed" : "Open", fontSize: 7, bold: r.approved, color: r.approved ? "#2f6b3f" : r.resp ? CLAY : MUTE, alignment: "center" },
        ]),
      ],
    },
    layout: { hLineWidth: (i: number, node: any) => (i === 0 ? 0 : i === 1 ? 1 : i === node.table.body.length ? 0.9 : 0.5), vLineWidth: () => 0, hLineColor: (i: number, node: any) => (i === 1 || i === node.table.body.length ? "#8a8f98" : LINE), paddingTop: () => 5, paddingBottom: () => 5, paddingLeft: (i: number) => (i === 0 ? 0 : 6), paddingRight: () => 6 },
    margin: [0, 4, 0, 6],
  };

  const content: any[] = [
    ...cover("Drawing Review Schedule", review.title || "Drawing Review", "Comment & response record", p.client?.name || "", review.createdOn),
    { text: "", pageBreak: "before" },
    ...section("Review record"),
    metaRow("Project", `${p.name}${p.code ? ` · ${p.code}` : ""}`),
    metaRow("Review", review.title || "Drawing review"),
    metaRow("Prepared", fmtDate(new Date().toISOString())),
    metaRow("Requests", `${rows.length} — ${responded} addressed, ${approved} approved`),
    ...(review.summary ? [{ text: review.summary, fontSize: 9.5, color: BODY, italics: true, lineHeight: 1.5, margin: [0, 6, 0, 0] }] : []),
    ...section(`Comment / response schedule — ${rows.length}`),
    rows.length ? table : para("No change requests recorded on this review."),
    { text: "This schedule records the client's review comments and the revised implementation of each. 'Approved' indicates the client has accepted the revised implementation.", fontSize: 8, color: MUTE, italics: true, lineHeight: 1.4, margin: [0, 4, 0, 0] },
  ];
  return baseDoc(content, `${p.code || p.name} — Review ${review.title}`, `${p.name} · Review schedule`);
}

export const downloadReviewSchedulePdf = (p: Project, review: DrawingReview) =>
  download(reviewScheduleDoc(p, review), `${(p.code || p.name).replace(/[^\w]+/g, "_")}_review_schedule.pdf`);

/* The RFI log — every Request for Information, its status and answer, in a
   construction-format schedule. */
export function rfiLogDoc(p: Project): any {
  resetSecs();
  const rfis = [...(p.rfis ?? [])].sort((a: RFI, b: RFI) => a.ref.localeCompare(b.ref, undefined, { numeric: true }));
  const open = rfis.filter((r) => r.status !== "closed").length;
  const metaRow = (label: string, value: string) => ({
    columns: [{ width: 96, ...kicker(label, { fontSize: 7, margin: [0, 1, 0, 0] }) }, { width: "*", text: value || "—", fontSize: 9.5, color: INK }],
    margin: [0, 0, 0, 5],
  });
  const th = (t: string, alignment?: string) => ({ text: t, fontSize: 7, color: MUTE, characterSpacing: 0.8, alignment });
  const table = {
    table: {
      headerRows: 1, widths: [40, "*", "*", 44, 44],
      body: [
        [th("RFI"), th("QUESTION"), th("ANSWER"), th("SHEET", "center"), th("STATUS", "center")],
        ...rfis.map((r) => [
          { stack: [{ text: r.ref, fontSize: 8.5, color: INK, bold: true }, { text: fmtDate(r.raisedOn), fontSize: 6.8, color: FAINT, margin: [0, 1, 0, 0] }] },
          { stack: [{ text: r.subject, fontSize: 8.5, color: INK, bold: true }, { text: r.question, fontSize: 8, color: BODY, lineHeight: 1.3, margin: [0, 1, 0, 0] }, { text: `${r.raisedBy}${r.dueOn ? ` · due ${fmtDate(r.dueOn)}` : ""}`, fontSize: 6.8, color: FAINT, margin: [0, 1, 0, 0] }] },
          { stack: [{ text: r.answer || "Awaiting answer", fontSize: 8.5, color: r.answer ? BODY : FAINT, italics: !r.answer, lineHeight: 1.3 }, ...(r.answeredBy ? [{ text: `${r.answeredBy}${r.answeredOn ? ` · ${fmtDate(r.answeredOn)}` : ""}`, fontSize: 6.8, color: FAINT, margin: [0, 1, 0, 0] }] : [])] },
          { text: r.sheetCode || "—", fontSize: 8, color: BODY, alignment: "center" },
          { text: r.status === "closed" ? "Closed" : r.status === "answered" ? "Answered" : "Open", fontSize: 7, color: r.status === "answered" ? "#2f6b3f" : r.status === "closed" ? MUTE : CLAY, alignment: "center" },
        ]),
      ],
    },
    layout: { hLineWidth: (i: number, node: any) => (i === 0 ? 0 : i === 1 ? 1 : i === node.table.body.length ? 0.9 : 0.5), vLineWidth: () => 0, hLineColor: (i: number, node: any) => (i === 1 || i === node.table.body.length ? "#8a8f98" : LINE), paddingTop: () => 5, paddingBottom: () => 5, paddingLeft: (i: number) => (i === 0 ? 0 : 6), paddingRight: () => 6 },
    margin: [0, 4, 0, 6],
  };
  const content: any[] = [
    ...cover("RFI Log", `${p.name} — Requests for Information`, "Construction-phase clarifications", p.client?.name || "", new Date().toISOString()),
    { text: "", pageBreak: "before" },
    ...section("RFI record"),
    metaRow("Project", `${p.name}${p.code ? ` · ${p.code}` : ""}`),
    metaRow("Prepared", fmtDate(new Date().toISOString())),
    metaRow("RFIs", `${rfis.length} total — ${open} open`),
    ...section(`RFI schedule — ${rfis.length}`),
    rfis.length ? table : para("No RFIs raised."),
  ];
  return baseDoc(content, `${p.code || p.name} — RFI Log`, `${p.name} · RFI log`);
}
export const downloadRfiLogPdf = (p: Project) =>
  download(rfiLogDoc(p), `${(p.code || p.name).replace(/[^\w]+/g, "_")}_RFI_log.pdf`);

/* A fee proposal for an opportunity that isn't a project yet — the document you
   send to win the work. Figures come from the estimator; nothing is invented. */
export function proposalDoc(lead: any, est?: Estimate | null): any {
  resetSecs();
  const cur = lead.currency || "USD";
  const fee = lead.fee ?? est?.total ?? 0;
  const metaRow = (label: string, value: string) => ({
    columns: [{ width: 96, ...kicker(label, { fontSize: 7, margin: [0, 1, 0, 0] }) }, { width: "*", text: value || "—", fontSize: 9.5, color: INK }],
    margin: [0, 0, 0, 5],
  });
  const stages = est?.phases ?? [];
  const stageTable = stages.length ? {
    table: {
      headerRows: 1, widths: ["*", 60, 90],
      body: [
        [{ text: "STAGE", fontSize: 7, color: MUTE, characterSpacing: 0.8 }, { text: "SHARE", fontSize: 7, color: MUTE, characterSpacing: 0.8, alignment: "right" }, { text: "FEE", fontSize: 7, color: MUTE, characterSpacing: 0.8, alignment: "right" }],
        ...stages.map((s: any) => [
          { text: s.label, fontSize: 9.5, color: INK },
          { text: `${Math.round((s.pct ?? 0) * 100)}%`, fontSize: 9, color: BODY, alignment: "right" },
          { text: money(s.amount ?? 0, cur), fontSize: 9, color: BODY, alignment: "right" },
        ]),
      ],
    },
    layout: { hLineWidth: (i: number, n: any) => (i === 0 ? 0 : i === 1 ? 1 : i === n.table.body.length ? 0.9 : 0.5), vLineWidth: () => 0, hLineColor: (i: number, n: any) => (i === 1 || i === n.table.body.length ? "#8a8f98" : LINE), paddingTop: () => 5, paddingBottom: () => 5, paddingLeft: (i: number) => (i === 0 ? 0 : 8) },
    margin: [0, 4, 0, 6],
  } : null;

  const content: any[] = [
    ...cover("Fee Proposal", lead.name || "Project", lead.type || "", lead.clientName || "", new Date().toISOString()),
    { text: "", pageBreak: "before" },
    ...section("The opportunity"),
    metaRow("Project", lead.name || "—"),
    metaRow("Client", [lead.clientName, lead.company].filter(Boolean).join(" · ") || "—"),
    ...(lead.type ? [metaRow("Type", lead.type)] : []),
    ...(lead.location ? [metaRow("Location", lead.location)] : []),
    ...(lead.budget ? [metaRow("Construction budget", money(lead.budget, cur))] : []),
    ...(lead.note ? [{ text: lead.note, fontSize: 9.5, color: BODY, italics: true, lineHeight: 1.5, margin: [0, 6, 0, 0] }] : []),
    ...section("Our fee"),
    { text: money(fee, cur), fontSize: 22, color: INK, margin: [0, 2, 0, 4] },
    ...(lead.budget && fee ? [para(`${((fee / lead.budget) * 100).toFixed(1)}% of the stated construction budget.`)] : []),
    ...(stageTable ? [{ text: "", margin: [0, 6, 0, 0] }, stageTable] : []),
    ...section("What's included"),
    ...(est?.lines?.length
      ? est.lines.map((l: any) => ({ columns: [{ width: "*", text: l.label, fontSize: 9.5, color: BODY }, { width: 90, text: money(l.amount, cur), fontSize: 9, color: BODY, alignment: "right" }], margin: [0, 0, 0, 4] }))
      : [para("Full architectural services through the design stages, as set out in the engagement to follow.")]),
    { text: "This proposal is an offer to provide the services described. It is valid for 30 days and becomes binding only on a signed letter of engagement.", fontSize: 8, color: MUTE, italics: true, lineHeight: 1.4, margin: [0, 12, 0, 0] },
    signBlock(lead.clientName || "Client", undefined, undefined, new Date().toISOString()),
  ];
  return baseDoc(content, `${lead.name} — Fee Proposal`, `${lead.name} · Fee proposal`);
}
export const downloadProposalPdf = (lead: any, est?: Estimate | null) =>
  download(proposalDoc(lead, est), `${String(lead.name || "proposal").replace(/[^\w]+/g, "_")}_proposal.pdf`);
